import React, { useEffect, useRef, useState } from 'react';
import { Hash, Lock, FileText, Users, Settings, Trash2, Upload, Download, Send, X, UserPlus, Shield } from 'lucide-react';
import { channelsAPI, usersAPI } from '../utils/api';
import { decodeEntities } from '../utils/postContent';

interface ChannelViewProps {
  channelId: number;
  currentUserId?: number;
  onChannelDeleted: () => void;
  onChanged?: () => void;
}

const pick = (o: any, ...keys: string[]) => {
  for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null) return o[k];
  return undefined;
};

const fmtBytes = (n?: number) => {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

type Tab = 'posts' | 'files' | 'members' | 'settings';

export default function ChannelView({ channelId, currentUserId, onChannelDeleted, onChanged }: ChannelViewProps) {
  const [channel, setChannel] = useState<any>(null);
  const [myRole, setMyRole] = useState<'owner' | 'member' | null>(null);
  const [tab, setTab] = useState<Tab>('posts');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [posts, setPosts] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);

  // compose
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);

  // file upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // members — 이름/이메일 검색 초대
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [memberMsg, setMemberMsg] = useState('');

  // settings
  const [editName, setEditName] = useState('');
  const [editVisibility, setEditVisibility] = useState<'private' | 'shared'>('private');
  const [savingSettings, setSavingSettings] = useState(false);

  const isOwner = myRole === 'owner';

  const loadDetail = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await channelsAPI.get(channelId);
      setChannel(res.data.channel);
      setMyRole(res.data.myRole || null);
      setEditName(res.data.channel?.name || '');
      setEditVisibility((res.data.channel?.visibility as any) || 'private');
    } catch (e: any) {
      if (e?.response?.status === 404 || e?.response?.status === 403) setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    try {
      const res = await channelsAPI.getPosts(channelId);
      setPosts(res.data.posts || []);
    } catch { /* ignore */ }
  };
  const loadFiles = async () => {
    try {
      const res = await channelsAPI.getFiles(channelId);
      setFiles(res.data.files || []);
    } catch { /* ignore */ }
  };
  const loadMembers = async () => {
    try {
      const res = await channelsAPI.getMembers(channelId);
      setMembers(res.data.members || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    setTab('posts');
    loadDetail();
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  useEffect(() => {
    if (tab === 'files') loadFiles();
    if (tab === 'members') loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const submitPost = async () => {
    if (!content.trim() && !title.trim()) return;
    setPosting(true);
    try {
      await channelsAPI.createPost(channelId, { title: title.trim() || undefined, content: content.trim() });
      setTitle('');
      setContent('');
      await loadPosts();
    } catch (e: any) {
      alert(e?.response?.data?.error || '글 작성에 실패했습니다.');
    } finally {
      setPosting(false);
    }
  };

  const deletePost = async (postId: number) => {
    if (!window.confirm('이 글을 삭제할까요?')) return;
    try {
      await channelsAPI.deletePost(channelId, postId);
      await loadPosts();
    } catch (e: any) {
      alert(e?.response?.data?.error || '삭제에 실패했습니다.');
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      await channelsAPI.uploadFile(channelId, file);
      await loadFiles();
    } catch (err: any) {
      setUploadError(err?.response?.data?.error || '업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteFile = async (fileId: number) => {
    if (!window.confirm('이 파일을 삭제할까요?')) return;
    try {
      await channelsAPI.deleteFile(channelId, fileId);
      await loadFiles();
    } catch (e: any) {
      alert(e?.response?.data?.error || '삭제에 실패했습니다.');
    }
  };

  // 디바운스 사람 검색 (멤버 탭에서만)
  useEffect(() => {
    if (tab !== 'members') return;
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await usersAPI.search(q);
        setSearchResults(res.data.users || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ, tab]);

  const inviteUser = async (userId: number) => {
    setMemberMsg('');
    try {
      await channelsAPI.addMember(channelId, userId);
      setSearchQ('');
      setSearchResults([]);
      await loadMembers();
    } catch (e: any) {
      setMemberMsg(e?.response?.data?.error || '초대에 실패했습니다.');
    }
  };

  const removeMember = async (userId: number) => {
    try {
      await channelsAPI.removeMember(channelId, userId);
      await loadMembers();
    } catch (e: any) {
      alert(e?.response?.data?.error || '제거에 실패했습니다.');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await channelsAPI.update(channelId, { name: editName.trim(), visibility: editVisibility });
      await loadDetail();
      onChanged?.();
    } catch (e: any) {
      alert(e?.response?.data?.error || '저장에 실패했습니다.');
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteChannel = async () => {
    if (!window.confirm('채널과 그 안의 모든 글·파일이 삭제됩니다. 계속할까요?')) return;
    try {
      await channelsAPI.remove(channelId);
      onChanged?.();
      onChannelDeleted();
    } catch (e: any) {
      alert(e?.response?.data?.error || '삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }
  if (notFound || !channel) {
    return (
      <div className="text-center py-16">
        <div className="text-gray-500 mb-2">채널에 접근할 수 없습니다.</div>
        <button onClick={onChannelDeleted} className="text-gray-900 underline text-sm">피드로 돌아가기</button>
      </div>
    );
  }

  const shared = channel.visibility === 'shared';

  const TabBtn = ({ id, icon, label }: { id: Tab; icon: React.ReactNode; label: string }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
        tab === id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto">
      {/* Channel header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6 mb-4">
        <div className="flex items-center gap-2 mb-1">
          {shared ? <Hash className="w-5 h-5 text-gray-500" /> : <Lock className="w-5 h-5 text-gray-500" />}
          <h1 className="text-xl font-bold text-gray-900">{channel.name}</h1>
          <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${shared ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {shared ? '공유' : '비공유'}
          </span>
        </div>
        {channel.description && <p className="text-sm text-gray-600 mt-1">{channel.description}</p>}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        <TabBtn id="posts" icon={<FileText className="w-4 h-4" />} label="글" />
        <TabBtn id="files" icon={<Upload className="w-4 h-4" />} label="파일" />
        {shared && <TabBtn id="members" icon={<Users className="w-4 h-4" />} label="멤버" />}
        {isOwner && <TabBtn id="settings" icon={<Settings className="w-4 h-4" />} label="설정" />}
      </div>

      {/* POSTS */}
      {tab === 'posts' && (
        <div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목 (선택)"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="이 채널에 남길 글이나 메모를 입력하세요…"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={submitPost}
                disabled={posting || (!content.trim() && !title.trim())}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {posting ? '올리는 중…' : '올리기'}
              </button>
            </div>
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">아직 글이 없습니다.</div>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => {
                const authorName = pick(p, 'author_name', 'authorName');
                const authorId = Number(pick(p, 'author_id', 'authorId'));
                const canDelete = isOwner || authorId === currentUserId;
                return (
                  <div key={p.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {pick(p, 'title') && <h3 className="font-semibold text-gray-900 mb-1">{p.title}</h3>}
                        <p className="text-gray-800 whitespace-pre-wrap break-words">{decodeEntities(p.content)}</p>
                        <p className="text-xs text-gray-400 mt-2">{authorName}</p>
                      </div>
                      {canDelete && (
                        <button onClick={() => deletePost(p.id)} className="ml-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="삭제">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* FILES */}
      {tab === 'files' && (
        <div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <input ref={fileInputRef} type="file" onChange={onPickFile} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {uploading ? '업로드 중…' : '파일 업로드'}
            </button>
            <p className="text-xs text-gray-400 mt-2">최대 25MB. 문서·이미지·데이터 파일을 채널에 보관합니다.</p>
            {uploadError && <p className="text-sm text-red-600 mt-2">{uploadError}</p>}
          </div>

          {files.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">아직 파일이 없습니다.</div>
          ) : (
            <div className="space-y-2">
              {files.map((f) => {
                const url = pick(f, 'file_url', 'fileUrl');
                const fname = pick(f, 'file_name', 'fileName') || '파일';
                const size = pick(f, 'size_bytes', 'sizeBytes');
                const uploader = Number(pick(f, 'uploaded_by', 'uploadedBy'));
                const uploaderName = pick(f, 'uploader_name', 'uploaderName');
                const canDelete = isOwner || uploader === currentUserId;
                return (
                  <div key={f.id} className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                    <FileText className="w-8 h-8 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{fname}</div>
                      <div className="text-xs text-gray-400">{[fmtBytes(size), uploaderName].filter(Boolean).join(' • ')}</div>
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded" title="열기">
                      <Download className="w-4 h-4" />
                    </a>
                    {canDelete && (
                      <button onClick={() => deleteFile(f.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="삭제">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MEMBERS (shared) */}
      {tab === 'members' && shared && (
        <div>
          {isOwner && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">멤버 초대</label>
              <div className="relative">
                <input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="이름 또는 이메일로 검색…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
                {searchQ.trim().length >= 2 && (searching || searchResults.length > 0) && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {searching ? (
                      <div className="px-3 py-2 text-sm text-gray-400">검색 중…</div>
                    ) : (() => {
                      const memberIds = new Set(members.map((m: any) => Number(pick(m, 'user_id', 'userId'))));
                      const results = searchResults.filter((u: any) => !memberIds.has(Number(u.id)));
                      if (results.length === 0) return <div className="px-3 py-2 text-sm text-gray-400">결과가 없습니다.</div>;
                      return results.map((u: any) => (
                        <button
                          key={u.id}
                          onClick={() => inviteUser(u.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                        >
                          <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-medium flex-shrink-0">
                            {(u.name || '?').charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
                              {u.name}
                              {(u.is_verified_doctor ?? u.isVerifiedDoctor) && <Shield className="w-3.5 h-3.5 text-green-500" />}
                            </div>
                            <div className="text-xs text-gray-400 truncate">{[u.specialization, u.hospital].filter(Boolean).join(' • ')}</div>
                          </div>
                          <UserPlus className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">이름은 부분검색, 이메일은 정확히 입력하세요.</p>
              {memberMsg && <p className="text-sm text-red-600 mt-2">{memberMsg}</p>}
            </div>
          )}

          <div className="space-y-2">
            {members.map((m) => {
              const uid = Number(pick(m, 'user_id', 'userId'));
              const role = pick(m, 'role');
              return (
                <div key={uid} className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {(pick(m, 'name') || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{pick(m, 'name') || `사용자 #${uid}`}</div>
                    <div className="text-xs text-gray-400 truncate">{[pick(m, 'specialization'), pick(m, 'hospital')].filter(Boolean).join(' • ')}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${role === 'owner' ? 'border border-gray-300 text-gray-900' : 'bg-gray-100 text-gray-600'}`}>
                    {role === 'owner' ? '소유자' : '멤버'}
                  </span>
                  {isOwner && role !== 'owner' && (
                    <button onClick={() => removeMember(uid)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="제거">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SETTINGS (owner) */}
      {tab === 'settings' && isOwner && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">채널 이름</label>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={80}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">공개 범위</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={editVisibility === 'private'} onChange={() => setEditVisibility('private')} />
                <Lock className="w-4 h-4 text-gray-500" /><span className="text-sm">비공유 (나만)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={editVisibility === 'shared'} onChange={() => setEditVisibility('shared')} />
                <Hash className="w-4 h-4 text-gray-500" /><span className="text-sm">공유 (초대 멤버)</span>
              </label>
            </div>
          </div>
          <div className="flex justify-between items-center pt-2">
            <button
              onClick={saveSettings}
              disabled={savingSettings || !editName.trim()}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              {savingSettings ? '저장 중…' : '저장'}
            </button>
            <button onClick={deleteChannel} className="flex items-center gap-1.5 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg">
              <Trash2 className="w-4 h-4" />채널 삭제
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
